import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import "./CSS/FoodMenu.css";

const FoodMenu = ({ onViewFullMenu, categories, apiError }) => {
  const sectionRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const currentSection = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".menu-item");
            items.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add("appear");
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  // Data transformation
  const menuCategories = useMemo(() => {
    const transformed = {};
    categories?.forEach((category) => {
      if (category.items && category.items.length > 0) {
        transformed[category.name] = category.items.map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          detailedDescription: item.detailed_description || item.description,
          price: item.price_display,
          cookingTime: item.cooking_time,
          spiceLevel: item.spice_level,
          ingredients: item.ingredients || [],
          image: item.image,
          is_available: item.is_available,
          cuisine_type: item.cuisine_type,
          food_type_name: item.food_type_name,
          calories: item.calories,
          is_chef_special: item.is_chef_special,
          is_popular: item.is_popular,
          created_at: item.created_at,
          updated_at: item.updated_at
        }));
      }
    });
    return transformed;
  }, [categories]);

  // Helper for category display name
  const getCategoryDisplayName = useCallback((categoryName) => {
    const category = categories?.find((cat) => cat.name === categoryName);
    return category ? category.display_name : categoryName;
  }, [categories]);

  // Modal handlers
  const openModal = useCallback((item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedItem(null);
      document.body.style.overflow = "unset";
    }, 300);
  }, []);

  const shareOnWhatsApp = useCallback((productName) => {
    const message = `I'm interested in ${productName} from Ali Darbar. Can you tell me more about it?`;
    const url = `https://wa.me/+917030808025?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  }, []);

  return (
    <section id="menu" className="section menu-section" ref={sectionRef}>
      <div className="container">
        <div className="section-title">
          <h2>Menu</h2>
          <p>Discover our most popular dishes from each category</p>
          <button
            className="btn btn-primary view-full-btn"
            onClick={onViewFullMenu}
          >
            View Full Menu
          </button>
        </div>

        {apiError && (
          <div className="api-error">
            <p>{apiError}</p>
            <button
              className="btn btn-outline"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Category Slideshows */}
        {Object.keys(menuCategories).map(categoryName =>
          menuCategories[categoryName].length > 0 && (
            <div key={categoryName} className="category-slideshow-section">
              <h3 className="category-slideshow-title">
                {getCategoryDisplayName(categoryName)}
              </h3>
              <Slideshow
                items={menuCategories[categoryName].filter(
                  item => item.is_popular || item.is_chef_special
                )}
                onItemClick={openModal}
                category={categoryName}
              />
            </div>
          )
        )}

        {/* Call to Action */}
        <div className="menu-cta">
          <h3>Want to see our complete menu?</h3>
          <p>Explore all our delicious dishes in our full menu</p>
          <button className="btn btn-primary" onClick={onViewFullMenu}>
            View Full Menu
          </button>
        </div>
      </div>

      {/* Food Item Modal */}
      {selectedItem && (
        <div
          className={`modal-overlay ${isModalOpen ? "active" : ""}`}
          onClick={closeModal}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>

            <div className="modal-image">
              <img src={selectedItem.image} alt={selectedItem.name} />
              {selectedItem.is_chef_special && (
                <div className="chef-special-badge">Chef's Special</div>
              )}
              {selectedItem.is_popular && (
                <div className="popular-badge">Popular</div>
              )}
            </div>

            <div className="modal-details">
              <h2>{selectedItem.name}</h2>
              <div className="modal-description">
                <p>{selectedItem.detailedDescription}</p>
              </div>
              <div className="modal-info-grid">
                <div className="info-item">
                  <span className="info-label">Cooking Time</span>
                  <span className="info-value">{selectedItem.cookingTime} mins</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Spice Level</span>
                  <span className="info-value">{selectedItem.spiceLevel}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Food Type</span>
                  <span className="info-value">{selectedItem.food_type_name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Cuisine</span>
                  <span className="info-value">{selectedItem.cuisine_type}</span>
                </div>
              
                <div className="info-item">
                  <span className="info-label">Availability</span>
                  <span
                    className={`info-value ${
                      selectedItem.is_available ? "available" : "unavailable"
                    }`}
                  >
                    {selectedItem.is_available ? "Available" : "Not Available"}
                  </span>
                </div>
              </div>
              {selectedItem.ingredients &&
                selectedItem.ingredients.length > 0 && (
                  <div className="ingredients-section">
                    <h4>Key Ingredients</h4>
                    <div className="ingredients-list">
                      {selectedItem.ingredients.map((ingredient, index) => (
                        ingredient && (
                          <span key={index} className="ingredient-tag">
                            {ingredient}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                )}
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={onViewFullMenu}>
                  View Full Menu
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => shareOnWhatsApp(selectedItem.name)}
                  disabled={!selectedItem.is_available}
                >
                  {selectedItem.is_available
                    ? "Share on WhatsApp"
                    : "Currently Unavailable"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Enhanced Slideshow Component
const Slideshow = React.memo(({ items, onItemClick, category }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const slideshowRef = useRef(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  useEffect(() => {
    if (items.length > 1) {
      const slideInterval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % items.length);
      }, 4000);
      return () => clearInterval(slideInterval);
    }
  }, [items.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % items.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  if (!items.length) return null;

  return (
    <div
      className="slideshow-container"
      ref={slideshowRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="slideshow-track"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`slide ${!item.is_available ? "unavailable" : ""}`}
            onClick={() => item.is_available && onItemClick(item)}
          >
            <div className="slide-image">
              <img src={item.image} alt={item.name} />
              <div className="slide-overlay">
                <button className="view-details-btn">
                  <span>View Details</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              {!item.is_available && (
                <div className="unavailable-badge">Not Available</div>
              )}
              {item.is_chef_special && (
                <div className="chef-special-badge">Chef's Special</div>
              )}
              {item.is_popular && (
                <div className="popular-badge">Popular</div>
              )}
            </div>
            <div className="slide-content">
              <div className="slide-header">
                <h4>{item.name}</h4>
              </div>
              <p>{item.description}</p>
              <div className="slide-meta">
                <span className="cooking-time">
                  ⏱️ {item.cookingTime} mins
                </span>
                <span className="spice-level">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <span
                      key={i}
                      className={`spice-dot ${
                        i < (getSpiceLevelNumber(item.spiceLevel) || 0) ? "active" : ""
                      }`}
                    />
                  ))}
                </span>
              </div>
              <div className="food-tags">
                <span className="food-tag">{item.food_type}</span>
                <span className="food-tag">{item.cuisine_type}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items.length > 1 && (
        <>
          <button className="slideshow-btn prev" onClick={prevSlide}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button className="slideshow-btn next" onClick={nextSlide}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="slideshow-indicators">
            {items.map((_, index) => (
              <button
                key={index}
                className={`indicator ${
                  index === currentSlide ? "active" : ""
                }`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
});

// Helper function to convert spice level to number
const getSpiceLevelNumber = (spiceLevel) => {
  const levels = {
    'mild': 1,
    'medium': 2,
    'hot': 3,
    'extra hot': 3
  };
  return levels[spiceLevel?.toLowerCase()] || 1;
};

export default FoodMenu;